import { useEffect, useState } from 'react';
import { getAppInsightsSummary, getCachedResponse } from '../api/client';
import type { AppInsightsSummary, Env } from '../types';

// Thin one-liner replacement for AppInsightsSummaryTile. Same data fetch
// (uses getAppInsightsSummary cache) but renders as a compact horizontal
// strip above the trace panels so the page feels less tile-heavy.

interface Props {
  env: Env;
  windowHours?: number;
  refreshToken?: number;
}

function formatPercent(v?: number): string {
  if (v === undefined) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

function formatDuration(ms?: number): string {
  if (ms === undefined) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(2)} s`;
  return `${(ms / 60_000).toFixed(2)} min`;
}

export default function AppInsightsSummaryStrip({ env, windowHours = 24, refreshToken = 0 }: Props) {
  const [data, setData] = useState<AppInsightsSummary | null>(() =>
    getCachedResponse<AppInsightsSummary>('app-insights-summary', { env, windowHours }),
  );
  const [loading, setLoading] = useState(data === null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAppInsightsSummary(env, windowHours)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        /* swallow — strip just goes empty on failure */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [env, windowHours, refreshToken]);

  if (loading && !data) return <div className="ai-summary-strip ai-summary-strip-loading">Loading {windowHours}h summary…</div>;
  if (!data || data.status !== 'ok') return null;

  const failureRate = data.failureRate ?? 0;
  const failClass = failureRate > 0.05 ? 'bad' : failureRate > 0.01 ? 'warn' : 'ok';
  const exceptionsClass = (data.exceptionCount ?? 0) > 0 ? 'bad' : 'ok';

  return (
    <div className="ai-summary-strip" aria-label={`Application Insights ${windowHours}-hour summary for ${env.toUpperCase()}`}>
      <span className="ai-stat">
        <span className="ai-stat-label">{windowHours}h Requests</span>
        <span className="ai-stat-value">{data.requestCount ?? 0}</span>
      </span>
      <span className="ai-stat">
        <span className="ai-stat-label">Avg</span>
        <span className="ai-stat-value">{formatDuration(data.avgDurationMs)}</span>
      </span>
      <span className={`ai-stat ${failClass}`}>
        <span className="ai-stat-label">Fail rate</span>
        <span className="ai-stat-value">{formatPercent(data.failureRate)}</span>
      </span>
      <span className={`ai-stat ${exceptionsClass}`}>
        <span className="ai-stat-label">Exceptions</span>
        <span className="ai-stat-value">{data.exceptionCount ?? 0}</span>
      </span>
    </div>
  );
}
